/**
 * Shared helpers for maintenance list → form toolbar arrow navigation (V10).
 * Benchmark: mntn001 passes context; mntn002 consumes via navigationContextToQueryParams.
 */

/**
 * @param {{ table?: object|null, cleanSorters?: (sorters: unknown[]) => unknown[], toolbarSearch?: string, extra?: Record<string, unknown> }} [opts]
 * @returns {{ filters: unknown[], sorters: unknown[], toolbarSearch?: string, extra?: Record<string, unknown> }}
 */
export function buildNavigationContextFromTable({ table, cleanSorters, toolbarSearch, extra } = {}) {
  const filters = table?.getHeaderFilters?.() ?? [];
  const rawSorters = table?.getSorters?.() ?? [];
  const sorters =
    typeof cleanSorters === 'function' ? cleanSorters(rawSorters) : rawSorters;

  const context = { filters, sorters };

  const trimmedSearch = String(toolbarSearch ?? '').trim();
  if (trimmedSearch !== '') {
    context.toolbarSearch = trimmedSearch;
  }

  if (extra != null && typeof extra === 'object' && !Array.isArray(extra)) {
    const extraCopy = {};
    Object.keys(extra).forEach((key) => {
      const value = extra[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        extraCopy[key] = value;
      }
    });
    if (Object.keys(extraCopy).length > 0) {
      context.extra = extraCopy;
    }
  }

  return context;
}

/**
 * @param {string|number|boolean|object|null|undefined} value
 * @returns {string}
 */
function extraValueToQueryString(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value).trim();
  return str;
}

/**
 * @param {object|null|undefined} navigationContext
 * @returns {string} query string without leading &, suitable for URL append
 */
export function navigationContextToQueryParams(navigationContext) {
  if (navigationContext == null || typeof navigationContext !== 'object') {
    return '';
  }

  const p = new URLSearchParams();

  (navigationContext.filters || []).forEach((f) => {
    if (f?.field == null) return;
    const value = f.value;
    if (value == null || String(value).trim() === '') return;
    p.set(`filter_${f.field}`, value);
  });

  const sorters = navigationContext.sorters;
  if (Array.isArray(sorters) && sorters.length > 0) {
    p.set('sorters', JSON.stringify(sorters));
  }

  const toolbarSearch = String(navigationContext.toolbarSearch ?? '').trim();
  if (toolbarSearch !== '') {
    p.set('toolbar_search', toolbarSearch);
  }

  const extra = navigationContext.extra;
  if (extra != null && typeof extra === 'object' && !Array.isArray(extra)) {
    Object.keys(extra).forEach((key) => {
      const serialized = extraValueToQueryString(extra[key]);
      if (serialized !== '') {
        p.set(key, serialized);
      }
    });
  }

  return p.toString();
}
